import { useEffect, useRef } from 'react';
import audioButton from '@/assets/marketing/audio-button.svg';
import { IconHeart } from '@/components/icons';
import { resolveFlashcardFrame } from '@/lib/flashcard-frames';
import type { LearningCard } from '@/content/mobile';

type FlashCardDeckProps = {
  cards: LearningCard[];
  title: string;
  index: number;
  onIndexChange: (index: number) => void;
  favorites: string[];
  onToggleFavorite: () => void;
  onPlayAudio: () => void;
  canPlayAudio: boolean;
  onExit: () => void;
};

export function FlashCardDeck({
  cards,
  title,
  index,
  onIndexChange,
  favorites,
  onToggleFavorite,
  onPlayAudio,
  canPlayAudio,
  onExit,
}: FlashCardDeckProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pageCount = cards.length + 1;
  const onDonePage = index >= cards.length;
  const card = !onDonePage ? cards[index] : undefined;
  const favorited = Boolean(card && favorites.includes(card.persistencyKey));

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;
    const width = node.clientWidth;
    if (!width) return;
    const target = index * width;
    if (Math.abs(node.scrollLeft - target) > 4) {
      node.scrollTo({ left: target, behavior: 'smooth' });
    }
  }, [index]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onIndexChange(Math.min(index + 1, pageCount - 1));
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onIndexChange(Math.max(index - 1, 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, onIndexChange, pageCount]);

  const handleScroll = () => {
    const node = scrollerRef.current;
    if (!node) return;
    const width = node.clientWidth;
    if (!width) return;
    const next = Math.round(node.scrollLeft / width);
    if (next !== index && next >= 0 && next < pageCount) onIndexChange(next);
  };

  return (
    <div className="relative flex min-h-[calc(100dvh-6.5rem)] flex-col lg:min-h-[calc(100dvh-2rem)]">
      <header className="flex items-center justify-between gap-3 px-5 pb-2 pt-5 sm:px-8">
        <button
          type="button"
          onClick={onExit}
          className="font-bold text-[#2d7a13]"
        >
          ← {title}
        </button>
        <p className="text-sm font-extrabold text-slate-500" aria-live="polite">
          {onDonePage ? 'Done' : `${index + 1} / ${cards.length}`}
        </p>
      </header>

      <div
        ref={scrollerRef}
        data-testid="flashcard-pager"
        className="flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onScroll={handleScroll}
      >
        {cards.map((item, cardIndex) => (
          <FlashCardSlide
            key={item.persistencyKey || `${item.title}-${cardIndex}`}
            card={item}
            cardIndex={cardIndex}
            canPlayAudio={canPlayAudio && index === cardIndex}
            onPlayAudio={onPlayAudio}
          />
        ))}
        <LastCardSlide onStartOver={() => onIndexChange(0)} onExit={onExit} />
      </div>

      {card ? (
        <button
          type="button"
          onClick={onToggleFavorite}
          aria-label={favorited ? 'Remove favorite' : 'Add favorite'}
          aria-pressed={favorited}
          className={`absolute bottom-6 right-5 z-10 grid h-14 w-14 place-items-center rounded-full shadow-lg transition sm:right-8 ${
            favorited ? 'bg-red-500 text-white' : 'bg-[#d4d4d4] text-white'
          }`}
        >
          <IconHeart size={22} filled={favorited} />
        </button>
      ) : null}
    </div>
  );
}

function FlashCardSlide({
  card,
  cardIndex,
  canPlayAudio,
  onPlayAudio,
}: {
  card: LearningCard;
  cardIndex: number;
  canPlayAudio: boolean;
  onPlayAudio: () => void;
}) {
  const frame = resolveFlashcardFrame(card.cardColor, cardIndex);
  const isNumber = /^\d+$/.test(card.title.trim());
  // Reserve the speaker slot on every card that has audio so the copy does
  // not jump when a slide becomes the active (playable) one mid-swipe.
  const hasAudio = Boolean(card.sound || card.soundChildren);
  const titleSize = isNumber
    ? card.title.length > 1
      ? 'text-7xl sm:text-8xl'
      : 'text-8xl sm:text-[7.5rem]'
    : card.title.length > 13
      ? 'text-2xl sm:text-3xl'
      : 'text-4xl sm:text-5xl';

  return (
    <section className="flex w-full shrink-0 snap-center items-center justify-center px-4 py-2 sm:px-8">
      <article
        className="relative aspect-[375/645] w-full max-w-[22rem] overflow-hidden rounded-[1.5rem] shadow-[0_18px_40px_rgba(31,86,16,0.12)] sm:max-w-[24rem]"
        style={{ color: frame.ink }}
      >
        <img src={frame.frame} alt="" className="absolute inset-0 h-full w-full object-fill" />
        <div className="relative flex h-full flex-col items-center justify-center px-10 py-12 text-center">
          {card.image ? (
            <img src={card.image} alt="" className="mb-5 max-h-44 max-w-[70%] object-contain sm:max-h-52" />
          ) : null}
          <h2 className={`font-nunito font-bold leading-tight ${titleSize}`}>{card.title}</h2>
          {card.translation ? (
            <p className="mt-2 font-nunito text-[26px] italic font-normal">{card.translation}</p>
          ) : null}
          {card.subtitle ? <p className="mt-2 font-nunito text-lg font-bold opacity-80">{card.subtitle}</p> : null}
          {/* Mobile parity (BaseFlashCard): the speaker sits in the column
              directly under the copy, not pinned to the card's bottom edge. */}
          {canPlayAudio ? (
            <button
              type="button"
              onClick={onPlayAudio}
              aria-label={`Hear ${card.title}`}
              className="mt-8 h-11 w-11 shrink-0"
              style={{ color: frame.speaker }}
            >
              <img src={audioButton} alt="" className="h-full w-full" />
            </button>
          ) : hasAudio ? (
            <span aria-hidden className="mt-8 h-11 w-11 shrink-0" />
          ) : null}
        </div>
      </article>
    </section>
  );
}

function LastCardSlide({ onStartOver, onExit }: { onStartOver: () => void; onExit: () => void }) {
  return (
    <section className="flex w-full shrink-0 snap-center items-center justify-center px-6 py-8">
      <div className="w-full max-w-md text-center">
        <p className="text-lg leading-7 text-slate-700">
          Great job going through the flashcards! We hope you learned something.
          <br />
          <br />
          If not, start over from the beginning.
        </p>
        <button
          type="button"
          onClick={onStartOver}
          className="mt-10 h-16 w-full max-w-xs rounded-xl bg-[#399918] text-xl font-bold text-white"
        >
          Start Over
        </button>
        <button
          type="button"
          onClick={onExit}
          className="mt-4 h-16 w-full max-w-xs rounded-xl bg-[#D9D9D9] text-xl font-bold text-hmong-ink"
        >
          Exit
        </button>
      </div>
    </section>
  );
}
